/**
 * ForgeNode — FORGE pattern proposal rendered on the Environment
 * surface, parity-fed from the same `/api/v1/forge/...` endpoint as
 * Workspace `ForgeLane.tsx`.
 *
 * Cycle-102 sprint-3 Task 3.3 (sprint plan §449-453).
 *
 * AC#10: same `source_group=forge_pattern` data as Workspace ForgeLane
 * when fetched simultaneously; per-surface dismiss state — operator
 * dismisses pattern X in Workspace, X still visible in Environment.
 *
 * Visual: sprint-2 forge-circle shape (`NodeShapeRoot type='forge'`)
 * + headline + severity chip + dismiss control. Compact card meant for
 * mounting in a side rail next to the Environment canvas.
 */

import { useCallback, useMemo } from 'react';

import { cn } from '../../../lib/utils';
import { NodeShapeRoot } from '../canvas/nodeShapes/NodeShapeRoot';
import {
  STATUS_DOT_VAR,
  type NodeStatus,
} from '../canvas/nodeShapes/nodeMaps';
import { useForgePatterns } from '../../../hooks/useForgePatterns';
import { useDismissedPatterns } from '../../../hooks/useDismissedPatterns';
import type { PatternProposal, PatternSeverity } from '../../../api/forge';

interface ForgeNodeListProps {
  /** Cap on the number of cards rendered. Defaults to 6. */
  limit?: number;
  className?: string;
}

const SEVERITY_TO_STATUS: Record<PatternSeverity, NodeStatus> = {
  routine: 'idle',
  notable: 'queued',
  significant: 'evaluating',
  critical: 'failed',
};

const SEVERITY_LABEL: Record<PatternSeverity, string> = {
  routine: 'Routine',
  notable: 'Notable',
  significant: 'Significant',
  critical: 'Critical',
};

export function ForgeNodeList({ limit = 6, className }: ForgeNodeListProps) {
  const { data, isLoading, isError, refetch } = useForgePatterns('world');
  const { dismissed, dismiss, restore } = useDismissedPatterns({
    surface: 'environment',
  });

  const visible = useMemo(() => {
    if (!data) return [];
    return (data.patterns ?? [])
      .filter((p) => !dismissed.has(p.pattern_id))
      .slice(0, limit);
  }, [data, dismissed, limit]);

  const dismissedCount = dismissed.size;

  return (
    <section
      role="region"
      aria-label="Environment FORGE patterns"
      data-testid="forge-node-list"
      className={cn(
        'flex flex-col gap-2 rounded-md border border-[var(--border-subtle)] bg-[var(--bg-card)]/80 p-3',
        className,
      )}
    >
      <header className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--fg-muted)]">
          FORGE · environment
        </span>
        {dismissedCount > 0 && (
          <button
            type="button"
            data-testid="forge-node-list-restore-all"
            onClick={() => restore()}
            className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded border border-[var(--border-subtle)] text-[var(--fg-muted)] hover:text-[var(--fg)] hover:bg-[var(--bg-hover)]
              focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--sig-iris)]"
          >
            Restore ({dismissedCount})
          </button>
        )}
      </header>

      {isLoading && !data && (
        <p
          role="status"
          aria-live="polite"
          className="text-[10px] font-mono text-[var(--fg-muted)]"
        >
          Loading FORGE patterns…
        </p>
      )}
      {isError && !data && (
        <div role="alert" data-testid="forge-node-list-error" className="flex flex-col gap-1">
          <span className="text-[10px] font-mono text-amber-400">
            FORGE patterns unavailable
          </span>
          <button
            type="button"
            onClick={() => refetch()}
            className="self-start text-[10px] font-mono text-[var(--fg-secondary)] underline"
          >
            Retry
          </button>
        </div>
      )}
      {data && visible.length === 0 && dismissedCount === 0 && (
        <p
          role="status"
          className="text-[10px] font-mono text-[var(--fg-muted)]"
        >
          No active FORGE patterns.
        </p>
      )}
      {visible.map((pattern) => (
        <ForgeNodeCard
          key={pattern.pattern_id}
          pattern={pattern}
          onDismiss={() => dismiss(pattern.pattern_id)}
        />
      ))}
    </section>
  );
}

function ForgeNodeCard({
  pattern,
  onDismiss,
}: {
  pattern: PatternProposal;
  onDismiss: () => void;
}) {
  const status = SEVERITY_TO_STATUS[pattern.severity];
  const accent = STATUS_DOT_VAR[status];
  const handleActivate = useCallback(() => {
    // sprint-3 wires no Environment-side promote action; sprint-4 will.
  }, []);
  return (
    <article
      data-testid={`forge-node-${pattern.pattern_id}`}
      className="flex items-start gap-2 rounded-md border border-[var(--border-subtle)] bg-[var(--bg-elevated)]/40 p-2"
    >
      <svg width={36} height={36}>
        <NodeShapeRoot
          id={`forge-${pattern.pattern_id}`}
          type="forge"
          status={status}
          cx={18}
          cy={18}
          radius={12}
          label={`FORGE pattern ${pattern.kind} (${pattern.severity})`}
          onActivate={handleActivate}
        />
      </svg>
      <div className="flex flex-1 flex-col gap-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-mono text-[var(--fg)] truncate">
            {pattern.kind}
          </span>
          <span
            className="text-[9px] font-mono uppercase tracking-widest"
            style={{ color: accent }}
            data-testid={`forge-node-${pattern.pattern_id}-severity`}
          >
            {SEVERITY_LABEL[pattern.severity]}
          </span>
        </div>
        <p className="text-[10px] text-[var(--fg-secondary)] line-clamp-2">{pattern.prose}</p>
        <div className="flex items-center justify-between gap-2 text-[9px] font-mono text-[var(--fg-muted)]">
          <span>conf {pattern.confidence.toFixed(2)}</span>
          <button
            type="button"
            data-testid={`forge-node-${pattern.pattern_id}-dismiss`}
            onClick={onDismiss}
            className="px-1.5 py-0.5 rounded border border-[var(--border-subtle)] hover:text-[var(--fg)] hover:bg-[var(--bg-hover)]
              focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--sig-iris)]"
          >
            Dismiss
          </button>
        </div>
      </div>
    </article>
  );
}
